/**
 * EnergyHeroKpiCards.tsx
 *
 * Five hero KPI cards for the Energieprestaties board dashboard.
 * Replaces the old EnergyPerformanceSummaryCards overview group.
 *
 * Cards:
 * 1. Totaal Geïnvesteerd — planned/actueel toggle
 * 2. Gemiddelde EPC — derived from row data
 * 3. Units Verbeterd — unitsImproved / totalUnitsWithEPC
 * 4. Acties Nodig — unitsNeedingNewEpc + count energetic
 * 5. Energetisch Aandeel — energeticShareOfBudget %
 *
 * Clicking a card fires onFilterClick which mirrors the handleMetricFilter
 * plumbing already in energieprestaties.tsx.
 */
import { useMemo } from "react";
import {
  CurrencyEur,
  Gauge,
  TrendUp,
  Warning,
  Lightning,
} from "@phosphor-icons/react";
import {
  EPCData,
  EPCLabel,
  EPCMetrics,
  getEPCColor,
  getDisplayLabel,
} from "../hooks/useEnergyPerformanceData";
import { formatEuroCompact } from "shared/components/renovation/helpers/formatCurrency";

// ─── Types ────────────────────────────────────────────────────────────────────

export type InvestmentMode = "planned" | "actueel";

interface EnergyHeroKpiCardsProps {
  epcData: EPCData[];
  metrics: EPCMetrics | null;
  avgEPCLabel: EPCLabel | null;
  activeFilter: string | null;
  investmentMode: InvestmentMode;
  onInvestmentModeChange: (mode: InvestmentMode) => void;
  onFilterClick: (filter: string) => void;
}

// ─── Sub-card ─────────────────────────────────────────────────────────────────

interface HeroCardProps {
  filterKey: string;
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
  children: React.ReactNode;
}

function HeroCard({
  filterKey,
  activeFilter,
  onFilterClick,
  children,
}: HeroCardProps) {
  const isActive = activeFilter === filterKey;
  return (
    <div
      className={`flex cursor-pointer flex-col gap-2 rounded-lg border p-4 transition-all duration-200 hover:shadow-sm ${
        isActive
          ? "border-moss-primary bg-moss-secondary-light ring-moss-primary ring-1"
          : "border-neutral-primary/15 hover:border-neutral-primary/30 bg-white"
      }`}
      onClick={() => onFilterClick(filterKey)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onFilterClick(filterKey)}
    >
      {children}
    </div>
  );
}

// ─── Card: Totaal Geïnvesteerd ────────────────────────────────────────────────

interface TotaalGeïnvesteerdCardProps {
  metrics: EPCMetrics | null;
  investmentMode: InvestmentMode;
  onInvestmentModeChange: (mode: InvestmentMode) => void;
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
}

function TotaalGeïnvesteerdCard({
  metrics,
  investmentMode,
  onInvestmentModeChange,
  activeFilter,
  onFilterClick,
}: TotaalGeïnvesteerdCardProps) {
  const total =
    investmentMode === "planned"
      ? (metrics?.totalInvestedPlanned ?? 0)
      : (metrics?.totalInvestedActual ?? 0);

  const energeticShare = metrics?.energeticShareOfBudget ?? 0;
  const energeticPct = Math.round(energeticShare * 100);
  const otherPct = 100 - energeticPct;

  return (
    <HeroCard
      filterKey="invested"
      activeFilter={activeFilter}
      onFilterClick={onFilterClick}
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <CurrencyEur
            size={15}
            weight="duotone"
            className="text-neutral-secondary-mid flex-shrink-0"
          />
          <span className="text-neutral-secondary-mid text-xs font-medium uppercase tracking-wider">
            Totaal Geïnvesteerd
          </span>
        </div>
        {/* Planned / Actueel toggle — stop propagation so it doesn't fire the card filter */}
        <div
          className="flex items-center rounded-md border border-neutral-primary/15 text-xs"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className={`rounded-l-md px-2 py-0.5 transition-colors ${
              investmentMode === "planned"
                ? "bg-neutral-primary text-white"
                : "text-neutral-secondary-mid hover:bg-neutral-primary/5"
            }`}
            onClick={() => onInvestmentModeChange("planned")}
          >
            Gepland
          </button>
          <button
            className={`rounded-r-md px-2 py-0.5 transition-colors ${
              investmentMode === "actueel"
                ? "bg-neutral-primary text-white"
                : "text-neutral-secondary-mid hover:bg-neutral-primary/5"
            }`}
            onClick={() => onInvestmentModeChange("actueel")}
          >
            Actueel
          </button>
        </div>
      </div>

      {/* Main value */}
      <div className="text-neutral-primary text-2xl font-bold">
        {formatEuroCompact(total)}
      </div>

      {/* Energetisch pip */}
      {energeticPct > 0 && (
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-neutral-primary/10">
            <div
              className="bg-brick-primary h-full rounded-full"
              style={{ width: `${energeticPct}%` }}
            />
          </div>
          <span className="text-brick-primary shrink-0 text-xs font-medium">
            {energeticPct}% ⚡
          </span>
          <span className="text-neutral-secondary-mid shrink-0 text-xs">
            {otherPct}% overig
          </span>
        </div>
      )}
    </HeroCard>
  );
}

// ─── Card: Gemiddelde EPC ─────────────────────────────────────────────────────

function GemiddeldeEpcCard({
  avgEPCLabel,
  activeFilter,
  onFilterClick,
}: {
  avgEPCLabel: EPCLabel | null;
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
}) {
  return (
    <HeroCard
      filterKey="withEPC"
      activeFilter={activeFilter}
      onFilterClick={onFilterClick}
    >
      <div className="flex items-center gap-1.5">
        <Gauge
          size={15}
          weight="duotone"
          className="text-neutral-secondary-mid flex-shrink-0"
        />
        <span className="text-neutral-secondary-mid text-xs font-medium uppercase tracking-wider">
          Gemiddelde EPC
        </span>
      </div>
      <div className="flex items-center gap-2">
        {avgEPCLabel ? (
          <span
            className={`rounded-lg px-3 py-1 text-2xl font-bold ${getEPCColor(avgEPCLabel)}`}
          >
            {getDisplayLabel(avgEPCLabel)}
          </span>
        ) : (
          <span className="text-neutral-secondary-mid text-2xl font-bold">
            —
          </span>
        )}
      </div>
      <span className="text-neutral-secondary-mid text-xs">Portfolio gemiddelde</span>
    </HeroCard>
  );
}

// ─── Card: Units Verbeterd ────────────────────────────────────────────────────

function UnitsVerbeterdCard({
  metrics,
  activeFilter,
  onFilterClick,
}: {
  metrics: EPCMetrics | null;
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
}) {
  const improved = metrics?.unitsImproved ?? 0;
  const total = metrics?.totalUnitsWithEPC ?? 0;
  const pct = total > 0 ? Math.round((improved / total) * 100) : 0;

  return (
    <HeroCard
      filterKey="improved"
      activeFilter={activeFilter}
      onFilterClick={onFilterClick}
    >
      <div className="flex items-center gap-1.5">
        <TrendUp
          size={15}
          weight="duotone"
          className="text-neutral-secondary-mid flex-shrink-0"
        />
        <span className="text-neutral-secondary-mid text-xs font-medium uppercase tracking-wider">
          Units Verbeterd
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-moss-primary text-2xl font-bold">{improved}</span>
        <span className="text-neutral-secondary-mid text-sm">/ {total}</span>
      </div>
      <span className="text-neutral-secondary-mid text-xs">{pct}% van units met EPC</span>
    </HeroCard>
  );
}

// ─── Card: Acties Nodig ───────────────────────────────────────────────────────

function ActiesNodigCard({
  metrics,
  epcData,
  activeFilter,
  onFilterClick,
}: {
  metrics: EPCMetrics | null;
  epcData: EPCData[];
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
}) {
  const needsNewEpc = metrics?.unitsNeedingNewEpc ?? 0;
  const energeticCount = useMemo(
    () =>
      epcData.filter(
        (u) => u.epcRefreshSuggested && u.epcRefreshReason === "ENERGETIC",
      ).length,
    [epcData],
  );

  return (
    <HeroCard
      filterKey="needsNewEpc"
      activeFilter={activeFilter}
      onFilterClick={onFilterClick}
    >
      <div className="flex items-center gap-1.5">
        <Warning
          size={15}
          weight="duotone"
          className="text-neutral-secondary-mid flex-shrink-0"
        />
        <span className="text-neutral-secondary-mid text-xs font-medium uppercase tracking-wider">
          Acties Nodig
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-yellow-600 text-2xl font-bold">{needsNewEpc}</span>
        <span className="text-neutral-secondary-mid text-sm">nieuwe EPC's</span>
      </div>
      {energeticCount > 0 && (
        <span className="text-brick-primary text-xs font-medium">
          {energeticCount} dringend (energetisch)
        </span>
      )}
    </HeroCard>
  );
}

// ─── Card: Energetisch Aandeel ────────────────────────────────────────────────

function EnergetischAandeelCard({
  metrics,
  activeFilter,
  onFilterClick,
}: {
  metrics: EPCMetrics | null;
  activeFilter: string | null;
  onFilterClick: (filter: string) => void;
}) {
  const share = metrics?.energeticShareOfBudget ?? 0;
  const pct = Math.round(share * 100);

  return (
    <HeroCard
      filterKey="needsEnergeticEpc"
      activeFilter={activeFilter}
      onFilterClick={onFilterClick}
    >
      <div className="flex items-center gap-1.5">
        <Lightning
          size={15}
          weight="duotone"
          className="text-neutral-secondary-mid flex-shrink-0"
        />
        <span className="text-neutral-secondary-mid text-xs font-medium uppercase tracking-wider">
          Energetisch Aandeel
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-brick-primary text-2xl font-bold">{pct}%</span>
      </div>
      <span className="text-neutral-secondary-mid text-xs">van budget</span>
    </HeroCard>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function EnergyHeroKpiCards({
  epcData,
  metrics,
  avgEPCLabel,
  activeFilter,
  investmentMode,
  onInvestmentModeChange,
  onFilterClick,
}: EnergyHeroKpiCardsProps) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <TotaalGeïnvesteerdCard
        metrics={metrics}
        investmentMode={investmentMode}
        onInvestmentModeChange={onInvestmentModeChange}
        activeFilter={activeFilter}
        onFilterClick={onFilterClick}
      />
      <GemiddeldeEpcCard
        avgEPCLabel={avgEPCLabel}
        activeFilter={activeFilter}
        onFilterClick={onFilterClick}
      />
      <UnitsVerbeterdCard
        metrics={metrics}
        activeFilter={activeFilter}
        onFilterClick={onFilterClick}
      />
      <ActiesNodigCard
        metrics={metrics}
        epcData={epcData}
        activeFilter={activeFilter}
        onFilterClick={onFilterClick}
      />
      <EnergetischAandeelCard
        metrics={metrics}
        activeFilter={activeFilter}
        onFilterClick={onFilterClick}
      />
    </div>
  );
}
